import type { LegendListRef } from "@-/com.npmjs.legendapp__list";
import { Platform } from "@-/com.npmjs.react-native";
import { useRouteBodyRef } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { animationFrames } from "@-/dev.improve.haniverse.lib.scheduler";
import { useNavigationTreeFocus } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs._lib.focus";
import { useCallback, useEffect, type RefObject } from "react";

// A level takes several seconds to pass, leaving time to see the size of the curriculum.
const PREVIEW_SCROLL_PIXELS_PER_SECOND = 24;

/** Manual scrolling cancels the preview across grade changes and hidden-body releases. */
export function useLevelsPreviewAutoScroll({
  enabled,
  listRef,
  scope,
}: {
  enabled: boolean;
  listRef: RefObject<LegendListRef | null>;
  scope: string;
}) {
  const manuallyScrolled = useRouteBodyRef("levels-preview-manually-scrolled", false);
  const { focusVersion, isFocusedRef } = useNavigationTreeFocus();
  const stopOnManualScroll = useCallback(() => {
    // The preview stops on its next frame, before it scrolls again.
    manuallyScrolled.current = true;
  }, [manuallyScrolled]);

  useEffect(() => {
    if (!enabled || manuallyScrolled.current) {
      return;
    }
    let isCancelled = false;
    const advance = async () => {
      let lastTime: number | undefined;
      let offset: number | undefined;
      for await (const time of animationFrames()) {
        // The imperative focus ref stays current even when native screens freeze this React tree.
        if (isCancelled || manuallyScrolled.current || !isFocusedRef.current) {
          return;
        }
        const list = listRef.current;
        const state = list?.getState();
        const elapsed = lastTime === undefined ? 0 : Math.min(64, time - lastTime);
        lastTime = time;
        if (
          list !== null &&
          state !== undefined &&
          state.scrollLength > 0 &&
          state.contentLength > 0
        ) {
          const end = Math.max(0, state.contentLength - state.scrollLength);
          offset = Math.min(
            end,
            (offset ?? state.scroll) + (elapsed * PREVIEW_SCROLL_PIXELS_PER_SECOND) / 1000,
          );
          if (offset >= end) {
            return;
          }
          // Keep fractional progress locally: native scroll events may round sub-pixel offsets.
          // Drive the scroller directly so LegendList cannot defer a preview command until after a
          // manual drag. Its normal scroll events still update virtualization and the saved anchor.
          if (Platform.OS === "web") {
            const scroller: HTMLElement | null = list.getScrollableNode();
            scroller?.scrollTo({ behavior: "instant", top: offset });
          } else {
            const scroller = list.getNativeScrollRef();
            if (
              scroller !== null &&
              "scrollTo" in scroller &&
              typeof scroller.scrollTo === "function"
            ) {
              scroller.scrollTo({ animated: false, y: offset });
            }
          }
        }
      }
    };
    void advance();
    return () => {
      isCancelled = true;
    };
  }, [enabled, focusVersion, isFocusedRef, listRef, manuallyScrolled, scope]);

  return stopOnManualScroll;
}
